import { useState, useEffect } from 'react';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Hook to detect the current Tailwind CSS breakpoint.
 * Uses mobile-first approach.
 */
export function useBreakpoint() {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint | 'mobile'>('mobile');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= breakpoints['2xl']) setCurrentBreakpoint('2xl');
      else if (width >= breakpoints.xl) setCurrentBreakpoint('xl');
      else if (width >= breakpoints.lg) setCurrentBreakpoint('lg');
      else if (width >= breakpoints.md) setCurrentBreakpoint('md');
      else if (width >= breakpoints.sm) setCurrentBreakpoint('sm');
      else setCurrentBreakpoint('mobile');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isIPad = typeof navigator !== 'undefined' && (
    /iPad/i.test(navigator.userAgent) || 
    (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 0) ||
    (window.innerWidth === 1366 && window.innerHeight === 1024) ||
    (window.innerWidth === 1024 && window.innerHeight === 1366)
  );

  return {
    breakpoint: currentBreakpoint,
    isMobile: currentBreakpoint === 'mobile' || currentBreakpoint === 'sm',
    isTablet: currentBreakpoint === 'md',
    isDesktop: ['lg', 'xl', '2xl'].includes(currentBreakpoint),
    isIPad,
    isSmallScreen: ['mobile', 'sm', 'md', 'lg'].includes(currentBreakpoint) || (isIPad && window.innerWidth <= 1366),
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  };
}
